import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { profileApi, type CustomerAddressItem, type CustomerAddressPayload } from "@/api/modules/profile";
import AddressFormOverlay, { type AddressFormData } from "@/components/feature/AddressFormOverlay";
import { auth } from "@/lib/auth";
import { buildLoginRedirect } from "@/lib/navigation";
import { RequestError } from "@/lib/request";

type PageMode = "list" | "add" | "edit";

interface AddressViewItem extends AddressFormData {
  id: number;
  locationText: string;
}

const BUSINESS_KEYWORDS = /公司|大厦|写字楼|中心|广场|园区|办公/i;
const SCHOOL_KEYWORDS = /学校|学院|大学|校区/i;

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

function parseOptionalId(value: string | null) {
  if (!value) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function toNullableNumber(value: number | string | null | undefined) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeCoordinate(value: number | null | undefined) {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }
  return Number(value.toFixed(6));
}

function compactText(parts: Array<string | null | undefined>) {
  return parts
    .map((part) => part?.trim() ?? "")
    .filter(Boolean)
    .join("");
}

function inferAddressLabel(source: string) {
  if (BUSINESS_KEYWORDS.test(source)) {
    return { label: "公司", labelIcon: "ri-building-line" };
  }
  if (SCHOOL_KEYWORDS.test(source)) {
    return { label: "学校", labelIcon: "ri-book-2-line" };
  }
  if (source.trim()) {
    return { label: "家", labelIcon: "ri-home-4-line" };
  }
  return { label: "其他", labelIcon: "ri-map-pin-2-line" };
}

function mapAddressItem(item: CustomerAddressItem): AddressViewItem {
  const addressText = (item.address ?? "").trim();
  const regionText = compactText([item.province, item.city, item.district]);
  const locationText = compactText([regionText, addressText]);
  const { label, labelIcon } = inferAddressLabel(`${locationText}${item.house_number ?? ""}`);

  return {
    id: item.id,
    label,
    labelIcon,
    name: (item.contact ?? "").trim(),
    gender: item.gender === 2 ? "ms" : "mr",
    phone: (item.mobile ?? "").trim(),
    district: addressText || regionText,
    houseNum: (item.house_number ?? "").trim(),
    isDefault: item.is_default === 1,
    provinceId: item.province_id ?? null,
    cityId: item.city_id ?? null,
    districtId: item.district_id ?? null,
    provinceName: (item.province ?? "").trim(),
    cityName: (item.city ?? "").trim(),
    districtName: (item.district ?? "").trim(),
    longitude: toNullableNumber(item.longitude),
    latitude: toNullableNumber(item.latitude),
    locationText: locationText || addressText || regionText,
  };
}

function buildAddressPayload(data: AddressFormData, id?: number): CustomerAddressPayload {
  return {
    ...(id ? { id } : {}),
    contact: data.name.trim(),
    gender: data.gender === "ms" ? 2 : 1,
    mobile: data.phone.trim(),
    province_id: data.provinceId ?? null,
    city_id: data.cityId ?? null,
    district_id: data.districtId ?? null,
    province_name: data.provinceName?.trim() || undefined,
    city_name: data.cityName?.trim() || undefined,
    district_name: data.districtName?.trim() || undefined,
    address: data.district.trim(),
    house_number: data.houseNum.trim(),
    is_default: data.isDefault ? 1 : 0,
    longitude: normalizeCoordinate(data.longitude),
    latitude: normalizeCoordinate(data.latitude),
  };
}

function appendSelectedAddressId(target: string, addressId: number) {
  if (!target) {
    return `/order-confirm?selectedAddressId=${addressId}`;
  }

  if (typeof window !== "undefined") {
    try {
      const url = new URL(target, window.location.origin);
      url.searchParams.set("selectedAddressId", String(addressId));
      return `${url.pathname}${url.search}`;
    } catch {
      // fall through
    }
  }

  const separator = target.includes("?") ? "&" : "?";
  return `${target}${separator}selectedAddressId=${addressId}`;
}

export default function AddressPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [addresses, setAddresses] = useState<AddressViewItem[]>([]);
  const [mode, setMode] = useState<PageMode>("list");
  const [editTarget, setEditTarget] = useState<AddressViewItem | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [saving, setSaving] = useState(false);
  const [actingId, setActingId] = useState<number | null>(null);

  const selectionMode = searchParams.get("mode") === "select";
  const returnTo = searchParams.get("returnTo") || "/order-confirm";
  const currentSelectedId =
    parseOptionalId(searchParams.get("selectedId")) ??
    parseOptionalId(searchParams.get("selectedAddressId"));

  const pageTitle = selectionMode ? "选择地址" : "地址管理";
  const pageSubtitle = loading
    ? "正在加载地址..."
    : selectionMode
      ? "请选择确认订单要使用的上门地址"
      : `${addresses.length} 个上门地址`;

  const redirectToLogin = () => {
    navigate(buildLoginRedirect(`${location.pathname}${location.search}`), { replace: true });
  };

  const ensureAuthOrRedirect = () => {
    if (auth.getToken()) {
      return true;
    }
    redirectToLogin();
    return false;
  };

  const handleAuthError = (error: unknown) => {
    if (error instanceof RequestError && error.code === 1001) {
      redirectToLogin();
      return true;
    }
    return false;
  };

  const loadAddresses = async (showLoading = true) => {
    if (!ensureAuthOrRedirect()) {
      setLoading(false);
      return;
    }

    if (showLoading) {
      setLoading(true);
    }
    setPageError("");

    try {
      const result = await profileApi.getAddressList();
      setAddresses((result.usable ?? []).map(mapAddressItem));
    } catch (error) {
      if (handleAuthError(error)) {
        return;
      }
      setPageError(getErrorMessage(error, "地址加载失败，请稍后重试"));
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    void loadAddresses();
  }, []);

  const handleBack = () => {
    if (selectionMode) {
      navigate(returnTo, { replace: true });
      return;
    }
    navigate(-1);
  };

  const closeForm = () => {
    if (saving) {
      return;
    }
    setMode("list");
    setEditTarget(null);
    setSubmitError("");
  };

  const openAddForm = () => {
    setSubmitError("");
    setEditTarget(null);
    setMode("add");
  };

  const openEditForm = (address: AddressViewItem) => {
    setSubmitError("");
    setEditTarget(address);
    setMode("edit");
  };

  const handleSelectAddress = (addressId: number) => {
    navigate(appendSelectedAddressId(returnTo, addressId), { replace: true });
  };

  const handleSetDefault = async (id: number) => {
    setActingId(id);
    setPageError("");
    try {
      await profileApi.setDefaultAddress(id);
      await loadAddresses(false);
    } catch (error) {
      if (handleAuthError(error)) {
        return;
      }
      setPageError(getErrorMessage(error, "设置默认地址失败，请稍后重试"));
    } finally {
      setActingId(null);
    }
  };

  const handleDelete = async (id: number) => {
    setActingId(id);
    setPageError("");
    try {
      await profileApi.deleteAddress(id);
      setDeleteConfirm(null);
      await loadAddresses(false);
    } catch (error) {
      if (handleAuthError(error)) {
        return;
      }
      setPageError(getErrorMessage(error, "删除地址失败，请稍后重试"));
    } finally {
      setActingId(null);
    }
  };

  const handleSave = async (data: AddressFormData) => {
    setSaving(true);
    setSubmitError("");

    try {
      let createdAddressId: number | null = null;

      if (mode === "add") {
        createdAddressId = await profileApi.addAddress(buildAddressPayload(data));
      } else if (mode === "edit" && editTarget) {
        await profileApi.editAddress(buildAddressPayload(data, editTarget.id));
      }

      if (selectionMode && createdAddressId) {
        navigate(appendSelectedAddressId(returnTo, createdAddressId), { replace: true });
        return;
      }

      await loadAddresses(false);
      setMode("list");
      setEditTarget(null);
    } catch (error) {
      if (handleAuthError(error)) {
        return;
      }
      setSubmitError(getErrorMessage(error, "保存地址失败，请稍后重试"));
    } finally {
      setSaving(false);
    }
  };

  const emptyStateText = useMemo(
    () =>
      selectionMode
        ? {
            title: "还没有可用地址",
            desc: "先新增一个上门地址，确认订单页才能继续使用",
            button: "去新增地址",
          }
        : {
            title: "还没有地址",
            desc: "新增一个上门地址，后续下单会直接带入",
            button: "添加第一个地址",
          },
    [selectionMode],
  );

  return (
    <div
      className="customer-page-shell flex min-h-screen flex-col"
      style={{
        background: "#F5F4F1",
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
      }}
    >
      <div
        className="relative overflow-hidden px-5 pb-5"
        style={{
          background: "linear-gradient(150deg, #FFB07A 0%, #FF8040 55%, #FF6018 100%)",
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
        }}
      >
        <div
          className="absolute -top-8 -right-8 h-36 w-36 rounded-full"
          style={{ background: "rgba(255,255,255,0.06)" }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <button
            onClick={handleBack}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full cursor-pointer"
            style={{ background: "rgba(255,255,255,0.2)" }}
          >
            <i className="ri-arrow-left-s-line text-lg text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-base font-bold text-white">{pageTitle}</h1>
            <p className="mt-0.5 text-[11px]" style={{ color: "rgba(255,255,255,0.7)" }}>
              {pageSubtitle}
            </p>
          </div>
          <button
            onClick={openAddForm}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 cursor-pointer"
            style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)" }}
          >
            <i className="ri-add-line text-sm text-white" />
            <span className="text-xs font-semibold text-white">新增</span>
          </button>
        </div>
      </div>

      <div className="space-y-3 px-4 pt-4">
        {pageError ? (
          <div
            className="flex items-start gap-2 rounded-2xl px-4 py-3"
            style={{ background: "#FFF5F5", border: "1px solid #FFD8D8", color: "#D25B5B" }}
          >
            <i className="ri-error-warning-line mt-0.5 text-sm" />
            <div className="flex-1">
              <p className="text-sm font-semibold">地址数据加载异常</p>
              <p className="mt-1 text-xs leading-relaxed">{pageError}</p>
            </div>
            <button
              onClick={() => void loadAddresses()}
              className="whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold cursor-pointer"
              style={{ background: "#fff", color: "#D25B5B" }}
            >
              重试
            </button>
          </div>
        ) : null}

        {loading ? (
          <div className="rounded-2xl bg-white px-4 py-10 text-center" style={{ border: "1px solid #F0EAE0" }}>
            <div
              className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full"
              style={{ background: "#FFF4EC", color: "#FF9A3C" }}
            >
              <i className="ri-loader-4-line text-2xl animate-spin" />
            </div>
            <p className="text-sm font-semibold" style={{ color: "#BBAAA0" }}>正在同步你的地址数据</p>
          </div>
        ) : null}

        {!loading && addresses.length === 0 ? (
          <div className="flex flex-col items-center py-16">
            <div
              className="mb-4 flex h-16 w-16 items-center justify-center rounded-full"
              style={{ background: "#FFF4EC" }}
            >
              <i className="ri-map-pin-2-line text-3xl" style={{ color: "#FFB07A" }} />
            </div>
            <p className="mb-1 text-sm font-semibold" style={{ color: "#BBAAA0" }}>{emptyStateText.title}</p>
            <p className="text-xs" style={{ color: "#CCBBB0" }}>{emptyStateText.desc}</p>
            <button
              onClick={openAddForm}
              className="mt-5 whitespace-nowrap rounded-full px-6 py-3 text-sm font-bold text-white cursor-pointer"
              style={{ background: "linear-gradient(90deg, #FF5C00, #FF9A3C)" }}
            >
              {emptyStateText.button}
            </button>
          </div>
        ) : null}

        {!loading &&
          addresses.map((addr) => {
            const isSelected = selectionMode && currentSelectedId === addr.id;
            return (
              <div
                key={addr.id}
                className="overflow-hidden rounded-2xl"
                style={{
                  background: isSelected ? "#FFFAF6" : "#fff",
                  border: `1.5px solid ${isSelected ? "#FF6018" : addr.isDefault ? "#FF9A3C" : "#F0EAE0"}`,
                }}
              >
                {addr.isDefault ? (
                  <div
                    className="flex items-center gap-1.5 px-4 py-2"
                    style={{ background: "#FFF8F0", borderBottom: "1px solid #FFE8CC" }}
                  >
                    <i className="ri-shield-check-fill text-xs" style={{ color: "#FF6018" }} />
                    <span className="text-[11px] font-semibold" style={{ color: "#FF6018" }}>默认地址</span>
                  </div>
                ) : null}

                <div className="flex items-start gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="text-sm font-bold" style={{ color: "#1A1A1A" }}>{addr.name || "联系人"}</span>
                      <span className="text-xs" style={{ color: "#999" }}>{addr.gender === "mr" ? "先生" : "女士"}</span>
                      <span className="text-xs" style={{ color: "#C0B0A0" }}>{addr.phone || "-"}</span>
                    </div>
                    <p className="text-xs leading-relaxed" style={{ color: "#666" }}>
                      {addr.locationText || addr.district || "未填写地址"}
                      {addr.houseNum ? ` ${addr.houseNum}` : ""}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 px-4 py-2.5" style={{ borderTop: "1px solid #F5F2EE" }}>
                  {selectionMode ? (
                    <button
                      onClick={() => handleSelectAddress(addr.id)}
                      className="flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold cursor-pointer"
                      style={{ background: isSelected ? "#FFF4EC" : "#FFF8F0", color: "#FF6018" }}
                    >
                      <i className={isSelected ? "ri-check-line text-xs" : "ri-corner-right-up-line text-xs"} />
                      {isSelected ? "当前已选" : "选择这个地址"}
                    </button>
                  ) : !addr.isDefault ? (
                    <button
                      onClick={() => void handleSetDefault(addr.id)}
                      disabled={actingId === addr.id}
                      className="flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1.5 text-xs cursor-pointer disabled:cursor-not-allowed"
                      style={{ background: "#F8F6F3", color: "#999", opacity: actingId === addr.id ? 0.6 : 1 }}
                    >
                      <i className={`text-xs ${actingId === addr.id ? "ri-loader-4-line animate-spin" : "ri-map-pin-line"}`} />
                      设为默认
                    </button>
                  ) : (
                    <div className="rounded-full px-2.5 py-1.5 text-xs font-semibold" style={{ background: "#FFF4EC", color: "#FF6018" }}>
                      当前默认
                    </div>
                  )}
                  <div className="flex-1" />
                  <button
                    onClick={() => openEditForm(addr)}
                    className="flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs cursor-pointer"
                    style={{ background: "#FFF4EC", color: "#FF6018" }}
                  >
                    <i className="ri-edit-line text-xs" />
                    编辑
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(addr.id)}
                    disabled={actingId === addr.id}
                    className="flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs cursor-pointer disabled:cursor-not-allowed"
                    style={{ background: "#FFF5F5", color: "#E05C5C", opacity: actingId === addr.id ? 0.6 : 1 }}
                  >
                    <i className={`text-xs ${actingId === addr.id ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line"}`} />
                    删除
                  </button>
                </div>
              </div>
            );
          })}

        {!loading && addresses.length > 0 ? (
          <button
            onClick={openAddForm}
            className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-2xl py-4 text-sm font-semibold cursor-pointer"
            style={{ border: "1.5px dashed #FFD4A0", color: "#FF6018", background: "#FFFAF6" }}
          >
            <i className="ri-add-line text-base" />
            添加新地址
          </button>
        ) : null}

        <div className="flex items-start gap-2.5 rounded-xl p-3.5" style={{ background: "#F8F6F3" }}>
          <i className="ri-lightbulb-line mt-0.5 flex-shrink-0 text-sm" style={{ color: "#C0B0A0" }} />
          <p className="text-[11px] leading-relaxed" style={{ color: "#C0B0A0" }}>
            {selectionMode
              ? "当前地址页已经和确认订单页打通。选择后会直接回到确认订单页，并按所选地址重新结算距离与交通费。"
              : "地址数据已切到真实接口。确认订单页会直接复用这里的默认地址或首条地址逻辑，不再走本地假数据。"}
          </p>
        </div>
      </div>

      {deleteConfirm !== null ? (
        <div
          className="customer-fixed-shell fixed inset-y-0 z-50 flex w-full items-center justify-center px-5"
          style={{
            background: "rgba(0,0,0,0.4)",
          }}
        >
          <div className="w-full rounded-3xl bg-white p-6">
            <div
              className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full"
              style={{ background: "#FFF5F5" }}
            >
              <i className="ri-delete-bin-2-line text-xl" style={{ color: "#E05C5C" }} />
            </div>
            <p className="mb-1.5 text-center text-sm font-bold" style={{ color: "#1A1A1A" }}>确认删除这个地址？</p>
            <p className="mb-6 text-center text-xs leading-relaxed" style={{ color: "#999" }}>
              删除后无法恢复，但不会影响历史订单中已经保存的地址信息。
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={actingId === deleteConfirm}
                className="flex-1 whitespace-nowrap rounded-2xl py-3 text-sm font-semibold cursor-pointer disabled:cursor-not-allowed"
                style={{ background: "#F5F2EE", color: "#888", opacity: actingId === deleteConfirm ? 0.6 : 1 }}
              >
                取消
              </button>
              <button
                onClick={() => void handleDelete(deleteConfirm)}
                disabled={actingId === deleteConfirm}
                className="flex-1 whitespace-nowrap rounded-2xl py-3 text-sm font-bold text-white cursor-pointer disabled:cursor-not-allowed"
                style={{ background: "#E05C5C", opacity: actingId === deleteConfirm ? 0.6 : 1 }}
              >
                {actingId === deleteConfirm ? "删除中..." : "确认删除"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {mode === "add" || mode === "edit" ? (
        <AddressFormOverlay
          title={mode === "add" ? "新增地址" : "编辑地址"}
          initial={editTarget ?? undefined}
          saving={saving}
          submitError={submitError}
          onSave={(data) => void handleSave(data)}
          onClose={closeForm}
        />
      ) : null}
    </div>
  );
}
